import React, { useState, useEffect } from "react";
import {
    UserOutlined,
    MailOutlined,
    PhoneOutlined,
    HomeOutlined,
    CalendarOutlined,
    InfoCircleOutlined,
    TeamOutlined,
    BankOutlined,
    ClockCircleOutlined,
    DollarOutlined,
    AppstoreOutlined,
    FormOutlined
} from "@ant-design/icons";
import { Col, Row, Button, Input, Form, message, Tag } from "antd";
import CommonCard from "../../../global/card";
import { cn } from "../../../../utils/cn";
import { formatDateTime, formatLeadSource } from "../../../../utils/functions";
import { useGenericMutation } from "../../../../queries/use-generic-mutation";
import SubmitButton from "../../../common/submitButton";
import Spinner from "../../../global/spinner";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "../../../../lib/query-constants";

const EnhancedHotLeadsDetails = ({
    leadDetail,
    handleClose,
    keyParams
}) => {
    const [notes, setNotes] = useState("");
    const [form] = Form.useForm();
    const queryClient = useQueryClient();

    const { mutateAsync: saveExtraNotes, isPending: isSavingNotes } = useGenericMutation({
        endpoint: `leadinfo/notes`,
        onSuccess: () => {
            message.success("Notes saved successfully");
            queryClient.invalidateQueries({
                queryKey: QUERY_KEYS.hotLeads.lists({ ...keyParams }),
                type: "active"
            })
        }
    });

    useEffect(() => {
        if (leadDetail?.notes) {
            setNotes(leadDetail.notes);
            form.setFieldsValue({ notes: leadDetail.notes });
        } else {
            setNotes("");
            form.setFieldsValue({ notes: "" });
        }
    }, [leadDetail, form]);

    if (!leadDetail) return null;

    const handleSubmitNotes = async (values) => {
        if (!values.notes) return;
        await saveExtraNotes({
            leadId: leadDetail._id,
            notes: values.notes,
        });
    };

    const getIconForField = (label) => {
        const iconMap = {
            Name: <UserOutlined />,
            Email: <MailOutlined />,
            Phone: <PhoneOutlined />,
            Address: <HomeOutlined />,
            "Event Name": <InfoCircleOutlined />,
            "Event Type": <InfoCircleOutlined />,
            "Event Date": <CalendarOutlined />,
            "Event Time": <ClockCircleOutlined />,
            "Guest Count": <TeamOutlined />,
            Company: <BankOutlined />,
            "Imported At": <CalendarOutlined />,
            Budget: <DollarOutlined />,
            "Notes/Inquiry": <InfoCircleOutlined />,
            Source: <AppstoreOutlined />,
        };
        return iconMap[label] || <InfoCircleOutlined />;
    };

    const SectionHeader = ({ icon, title }) => (
        <div className="flex items-center gap-2 mb-3 sm:mb-4">
            {React.cloneElement(icon, { style: { color: "var(--primary-color, #1890ff)", fontSize: "clamp(14px, 4vw, 16px)" } })}
            <span style={{ fontSize: "clamp(13px, 4vw, 15px)", fontWeight: 600 }}>{title}</span>
        </div>
    );

    const InfoItem = ({ label, value, isTag, color, className }) => (
        <Col xs={24} sm={12}>
            <div className="flex flex-col">
                <span className="text-xs text-gray-500 flex items-center gap-2 mb-1">
                    <span style={{ color: "var(--primary-color, #1890ff)" }}>
                        {getIconForField(label)}
                    </span>
                    {label}
                </span>
                {isTag ? (
                    <Tag color={color || "blue"} className={cn("m-0 px-3 py-1 font-medium capitalize w-fit", className)}>
                        {value || "—"}
                    </Tag>
                ) : (
                    <span className={cn("text-sm font-semibold text-gray-900", className)}>{value || "—"}</span>
                )}
            </div>
        </Col>
    );

    return (
        <div className="sm:p-6 p-2 w-full min-h-full overflow-x-hidden" style={{ backgroundColor: "#f5f7fa" }}>
            <CommonCard
                cardHeadBodyClassName={{ head: "!px-6 !py-0", body: "!px-6 !py-0 !pb-4" }}
            >
                <Row gutter={[24, 0]}>
                    {/* Basic Information Section */}
                    <Col span={24} className="border-b border-gray-200 py-4">
                        <SectionHeader icon={<UserOutlined />} title="Basic Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem
                                label="Name"
                                value={leadDetail.fullName ? leadDetail.fullName : `${leadDetail.firstName} ${leadDetail.lastName}`}
                            />
                            <InfoItem label="Email" value={leadDetail?.email} />
                            <InfoItem label="Phone" value={leadDetail?.phone} />
                            <InfoItem label="Imported At" value={formatDateTime(leadDetail?.dateAsPerUserTimezone)} />
                        </Row>
                    </Col>

                    {/* Event Information Section */}
                    <Col span={24} className="py-4 border-b border-gray-200">
                        <SectionHeader icon={<CalendarOutlined />} title="Event Information" />
                        <Row gutter={[{ xs: 16, sm: 24 }, { xs: 12, sm: 16 }]}>
                            <InfoItem label="Event Name" value={leadDetail?.eventName} />
                            <InfoItem label="Event Type" value={leadDetail?.eventType} />
                            <InfoItem label="Event Date" value={leadDetail?.eventDate} />
                            <InfoItem label="Guest Count" value={leadDetail?.guestCount} />
                            <InfoItem label="Company" value={leadDetail?.company} />
                            <InfoItem label="Imported At" value={formatDateTime(leadDetail?.dateAsPerUserTimezone)} />
                            <InfoItem label="Source" value={formatLeadSource(leadDetail?.leadSource)} className="capitalize" />
                        </Row>
                    </Col>

                    {/* Notes/Inquiry Section */}
                    {leadDetail?.inquiry && (
                        <Col span={24} className="border-b border-gray-200 py-4">
                            <SectionHeader icon={<InfoCircleOutlined />} title="Notes/Inquiry" />
                            <div className="bg-white p-3 rounded-lg border border-gray-100 italic shadow-sm">
                                <p className="text-sm text-gray-700 whitespace-pre-wrap m-0">{leadDetail?.inquiry}</p>
                            </div>
                        </Col>
                    )}

                    {/* Extra Information Section (Notes Form) */}
                    <Col span={24} className="pt-4">
                        <SectionHeader icon={<FormOutlined />} title="Extra Information" />
                        <Form
                            form={form}
                            layout="vertical"
                            onFinish={handleSubmitNotes}
                        >
                            <Form.Item
                                name="notes"
                                label="You can add extra information regarding lead"
                                rules={[{ required: true, message: 'Please add some notes' }]}
                            >
                                <Input.TextArea rows={4} placeholder="Add notes here..." />
                            </Form.Item>
                            <Form.Item className="mb-0">
                                <Button
                                    htmlType="submit"
                                    className="!h-10 px-6 !text-primary !border-primary hover:!border-primary hover:!bg-primary hover:!text-white font-medium transition-all"
                                >
                                    {isSavingNotes ? (
                                        <Spinner className={"!border-white !w-4 !h-4 mr-2"} />
                                    ) : (
                                        <FormOutlined className="mr-2" />
                                    )}
                                    Save Notes
                                </Button>
                            </Form.Item>
                        </Form>

                    </Col>
                </Row>
            </CommonCard>
        </div>
    );
};

export default EnhancedHotLeadsDetails;
